
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { Container } from "@/components/ui/container";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  ArrowLeft,
  MessageCircle,
  MapPin,
  Calendar,
  Store,
  Info,
  ShoppingBag,
  Star as StarIcon,
  User as UserIcon,
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { formatDate } from "@/hooks/useProductDetail";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import UserRatings from "@/components/user/UserRatings";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import UserProductList from "@/components/user/UserProductList";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OptimizedImage } from "@/components/ui/OptimizedImage";
import { ShareMenu } from "@/components/product/ShareMenu";
import { DynamicSeoHead } from "@/components/common/DynamicSeoHead";
import { createStoreSchema, createBreadcrumbSchema } from "@/utils/schemaUtils";
import { generateStoreSeoTitle, generateStoreSeoDescription, createStoreBreadcrumbs } from "@/utils/seoUtils";
import { extractStoreId, createStoreUrl, validateStoreUrl } from "@/utils/slugUtils";

interface UserProfileData {
  id: string;
  username?: string;
  email?: string;
  full_name?: string;
  profile_image?: string;
  location?: string;
  created_at: string;
  updated_at: string;
  is_store?: boolean; 
  store_name?: string; 
  store_banner_url?: string; 
  store_description?: string; 
}

const UserProfile = () => {
  const [searchParams] = useSearchParams();
  const { idSlug } = useParams<{ idSlug: string }>();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("products");
  const [currentUrl, setCurrentUrl] = useState("");

  useEffect(() => {
    setCurrentUrl(window.location.href);
  }, []);

  // Handle both old query parameter format and new slug format
  const userId = idSlug ? extractStoreId(idSlug) : searchParams.get("user");

  useEffect(() => {
    if (userId && user && userId === user.id) {
      navigate("/profile");
    }
  }, [userId, user, navigate]);

  const {
    data: userProfile,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["userProfile", userId],
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      const { data, error: queryError } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();
      if (queryError) throw queryError;
      if (!data && userId) {
        toast({
          title: "Përdoruesi nuk u gjet",
          description: `Profili me ID ${userId} nuk ekziston.`,
          variant: "destructive",
        });
        navigate("/");
        return null;
      }
      return data as UserProfileData;
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5,
  });

  // Check if current URL format is correct and redirect if needed (for stores)
  useEffect(() => {
    if (userProfile && userProfile.is_store && idSlug && userId) {
      const currentPath = window.location.pathname;
      const storeName = userProfile.store_name || userProfile.username || '';
      const correctUrl = createStoreUrl(userId, storeName);
      
      // Only redirect if the URL doesn't match and we're not already on the correct format
      if (!validateStoreUrl(userId, storeName, currentPath)) {
        // Preserve query parameters when redirecting
        const search = window.location.search;
        navigate(correctUrl + search, { replace: true });
        return;
      }
    }
  }, [userProfile, idSlug, userId, navigate]);

  useEffect(() => {
    if (error && userId) {
      toast({
        title: "Gabim",
        description: (error as Error).message || "Dështoi ngarkimi i profilit të përdoruesit",
        variant: "destructive",
      });
    }
  }, [error, toast, userId]);

  // SEO data calculation
  const profileUrl = userProfile && userProfile.is_store && userId 
    ? `https://treguim.org${createStoreUrl(userId, userProfile.store_name || userProfile.username || '')}`
    : `https://treguim.org/user?user=${userId}`;

  const seoTitle = userProfile && userProfile.is_store 
    ? generateStoreSeoTitle(userProfile.store_name || userProfile.username || '', userProfile.location)
    : `${userProfile?.full_name || userProfile?.username || 'Profili'} | TreguIm`;

  const seoDescription = userProfile && userProfile.is_store
    ? generateStoreSeoDescription(
        userProfile.store_name || userProfile.username || '',
        userProfile.store_description,
        userProfile.location
      )
    : `Shiko profilin e ${userProfile?.full_name || userProfile?.username || 'përdoruesit'} në TreguIm.`;

  const profileSchema = userProfile && userProfile.is_store 
    ? createStoreSchema(userProfile, profileUrl)
    : null;

  const breadcrumbSchema = userProfile && userProfile.is_store && userId
    ? createBreadcrumbSchema(createStoreBreadcrumbs(
        userProfile.store_name || userProfile.username || '',
        userId
      ))
    : null;

  const profileImage = userProfile?.store_banner_url || userProfile?.profile_image || 
    `${window.location.origin}/sharing-default.jpg`;

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleStartChat = async () => {
    if (!user || !userProfile) return;

    try {
      if (user.id === userProfile.id) {
        toast({
          title: "Nuk mund të dërgoni mesazh",
          description: "Ju nuk mund t'i dërgoni mesazh vetes",
          variant: "destructive",
        });
        return;
      }

      const { data: existingChat, error: findError } = await supabase
        .from("chats")
        .select("id")
        .or(
          `and(buyer_id.eq.${user.id},seller_id.eq.${userProfile.id}),and(buyer_id.eq.${userProfile.id},seller_id.eq.${user.id})`
        )
        .limit(1)
        .single(); 

      if (findError && findError.code !== 'PGRST116') { 
         throw findError;
      }

      if (existingChat) {
        navigate(`/messages?chat=${existingChat.id}`);
        return;
      }

      const { data: newChat, error: createError } = await supabase
        .from("chats")
        .insert({
          buyer_id: user.id, 
          seller_id: userProfile.id, 
        })
        .select("id")
        .single();

      if (createError) throw createError;

      if (newChat) {
        navigate(`/messages?chat=${newChat.id}`);
      } else {
        throw new Error("Failed to create or retrieve chat.");
      }
    } catch (err: any) { 
      console.error("Error starting chat:", err);
      toast({
        title: "Gabim në fillimin e bisedës",
        description: err.message || "Dështoi fillimi i bisedës. Ju lutemi provoni përsëri.",
        variant: "destructive",
      });
    }
  };
  
  if (!userId && !isLoading) {
    return (
      <div className="flex flex-col min-h-screen">
        <Navbar />
        <Container className="flex-grow py-10 text-center">
          <p>Profili i përdoruesit nuk është specifikuar.</p>
          <Button onClick={() => navigate('/')} className="mt-4">Kthehu te faqja kryesore</Button>
        </Container>
        <Footer />
      </div>
    );
  }
  
  const StoreProfileSkeleton = () => (
    <div className="bg-white dark:bg-Charcoal-Gray">
      <Skeleton className="w-full h-48 md:h-64 lg:h-80 bg-Neutral-Gray/30" />
      <Container className="-mt-16 md:-mt-20 relative z-10">
        <div className="p-6 bg-white dark:bg-Charcoal-Gray rounded-lg shadow-xl flex flex-col md:flex-row items-center md:items-start gap-6">
            <Skeleton className="h-24 w-24 md:h-32 md:w-32 rounded-full border-4 border-white dark:border-Charcoal-Gray" />
            <div className="flex-1 text-center md:text-left space-y-3 mt-4 md:mt-0">
              <Skeleton className="h-8 w-3/4 mx-auto md:mx-0" />
              <Skeleton className="h-5 w-1/2 mx-auto md:mx-0" />
              <Skeleton className="h-5 w-1/3 mx-auto md:mx-0" />
            </div>
            <Button variant="outline" className="h-10 w-36 mt-4 md:mt-0" />
        </div>
      </Container>
      <Container className="py-10">
        <Skeleton className="h-10 w-1/3 mb-6" />
        <Skeleton className="h-64 w-full" />
      </Container>
    </div>
  );

  return (
    <div className="flex flex-col min-h-screen bg-Soft-Gray dark:bg-Dark-Purple">
      {userProfile && (
        <DynamicSeoHead
          title={seoTitle}
          description={seoDescription}
          ogType={userProfile.is_store ? "business.business" : "profile"}
          ogImage={profileImage}
          url={profileUrl}
          structuredData={[profileSchema, breadcrumbSchema].filter(Boolean)}
          entityName={userProfile.is_store ? userProfile.store_name : userProfile.full_name}
          location={userProfile.location}
        />
      )}
      
      <Navbar />
      <div className="flex-grow">
        <div className="flex justify-start items-center py-4 px-4 md:px-8 sticky top-0 z-20 bg-white/80 dark:bg-Dark-Purple/80 backdrop-blur-sm border-b dark:border-gray-700">
            
            <Button
              variant="outline"
              size="icon"
              onClick={handleGoBack}
              className="rounded-full mr-4"
              aria-label="Kthehu Mbrapa"
            >
              <ArrowLeft size={16} />
            </Button>
            <h1 className="text-xl md:text-2xl font-bold text-Charcoal-Gray dark:text-Pure-White truncate">
              {isLoading && !userProfile ? 'Ngarkim...' : (userProfile?.is_store ? (userProfile.store_name || "Dyqani") : (userProfile?.full_name || userProfile?.username || "Profili"))}
            </h1>
        </div>

        {isLoading && !userProfile ? ( 
            <StoreProfileSkeleton /> 
        ) : userProfile ? (
          userProfile.is_store ? (
            // STORE PROFILE LAYOUT
            <div className="bg-white dark:bg-Charcoal-Gray">
              {/* Store Banner - Full Width */}
              
              <div className="w-full h-48 md:h-64 lg:h-80 bg-Neutral-Gray relative group overflow-hidden">
                <OptimizedImage 
                  src={userProfile.store_banner_url || '/placeholder.svg'} 
                  alt={userProfile.store_name || "Store Banner"} 
                  className="w-full h-full object-cover transition-transform duration-500 ease-in-out group-hover:scale-110"
                  fallbackSrc="/placeholder.svg"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              </div>

              {/* Store Info Header - Overlapping Banner */}
              <Container className="-mt-16 md:-mt-20 relative z-10">
                <div className="p-6 bg-white dark:bg-Charcoal-Gray rounded-lg shadow-xl flex flex-col md:flex-row items-center md:items-start gap-6">
                  
                  <Avatar className="h-24 w-24 md:h-32 md:w-32 rounded-full border-4 border-white dark:border-Charcoal-Gray shadow-lg">
                    <AvatarImage
                      src={userProfile.profile_image || `https://avatar.vercel.sh/${userProfile.id}`}
                      alt={userProfile.store_name || "Store Logo"}
                    />
                    <AvatarFallback>{(userProfile.store_name || "S").charAt(0)}</AvatarFallback>
                  </Avatar>

                  <div className="flex-1 text-center md:text-left mt-4 md:mt-0">
                    <h2 className="text-3xl md:text-4xl font-bold text-Charcoal-Gray dark:text-Pure-White">
                      {userProfile.store_name || "Dyqan Pa Emër"}
                    </h2>
                    <div className="mt-1 flex items-center justify-center md:justify-start text-sm text-Primary-Purple dark:text-Light-Purple">
                        <Store size={18} className="mr-2" />
                        <span>Dyqan i Verifikuar (Placeholder)</span> 
                    </div>
                    <div className="mt-2 flex flex-col md:flex-row md:items-center gap-x-4 gap-y-1 text-sm text-Medium-Gray dark:text-Light-Gray">
                      <div className="flex items-center">
                        <Calendar size={16} className="mr-1" />
                        <span>Anëtar që nga {formatDate(userProfile.created_at)}</span>
                      </div>
                      {userProfile.location && (
                        <div className="flex items-center">
                          <MapPin size={16} className="mr-1" />
                          <span>{userProfile.location}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Buttons Section */}
                  <div className="flex flex-col md:flex-row gap-3 mt-4 md:mt-0">
                    {user && userProfile.id !== user.id && (
                      <Button
                        onClick={handleStartChat}
                        className="bg-Primary-Purple hover:bg-Secondary-Purple text-Pure-White px-6 py-3 w-full md:w-auto"
                        size="lg"
                      >
                        <MessageCircle size={18} className="mr-2" />
                        Dërgo Mesazh
                      </Button>
                    )}
                    <ShareMenu
                      title={userProfile.store_name || "Dyqan në Treguim"}
                      url={currentUrl}
                      text={userProfile.store_description || `Shiko profilin e dyqanit ${userProfile.store_name || ''} në Treguim!`}
                      image={userProfile.store_banner_url || userProfile.profile_image || `${window.location.origin}/sharing-default.jpg`}
                      buttonText="Ndaj Profilin"
                    />
                  </div>
                </div>
              </Container>

              {/* Tabs Section */}
              
              <Container className="py-10">
                <Tabs defaultValue="products" className="w-full" onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-3 md:w-auto md:inline-flex bg-Soft-Gray dark:bg-Dark-Purple mb-6">
                    <TabsTrigger value="products" className="data-[state=active]:bg-Primary-Purple data-[state=active]:text-Pure-White">
                      <ShoppingBag size={16} className="mr-2"/> Produktet
                    </TabsTrigger>
                    <TabsTrigger value="about" className="data-[state=active]:bg-Primary-Purple data-[state=active]:text-Pure-White">
                      <UserIcon size={16} className="mr-2"/> Rreth Nesh
                    </TabsTrigger>
                    <TabsTrigger value="reviews" className="data-[state=active]:bg-Primary-Purple data-[state=active]:text-Pure-White">
                      <StarIcon size={16} className="mr-2"/> Vlerësimet
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="products">
                    {activeTab === "products" && (
                      <UserProductList 
                        userId={userProfile.id} 
                        isStore={userProfile.is_store}
                        storeName={userProfile.store_name}
                        username={userProfile.username}
                        fullName={userProfile.full_name}
                      />
                    )}
                  </TabsContent>
                  <TabsContent value="about">
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center text-xl text-Charcoal-Gray dark:text-Pure-White">
                          <Info size={20} className="mr-2 text-Primary-Purple" />
                          Rreth Dyqanit {userProfile.store_name || ""}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {userProfile.store_description ? (
                          <p className="text-Medium-Gray dark:text-Light-Gray whitespace-pre-wrap">
                            {userProfile.store_description}
                          </p>
                        ) : (
                          <p className="text-Medium-Gray dark:text-Light-Gray">Ky dyqan nuk ka dhënë ende një përshkrim.</p>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                  <TabsContent value="reviews">
                    {activeTab === "reviews" && (
                      <UserRatings userId={userProfile.id} />
                    )}
                  </TabsContent>
                </Tabs>
              </Container>
            </div>
          ) : (
            // REGULAR USER PROFILE LAYOUT
            
            <Container className="py-10">
              <div className="bg-white dark:bg-Charcoal-Gray rounded-lg shadow p-6">
                <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                  <Avatar className="h-24 w-24">
                    <AvatarImage
                      src={userProfile.profile_image || `https://avatar.vercel.sh/${userProfile.username || userProfile.id}`}
                      alt={userProfile.full_name || userProfile.username || "User"}
                    />
                    <AvatarFallback>{(userProfile.full_name || userProfile.username || "U").charAt(0)}</AvatarFallback>
                  </Avatar>

                  <div className="flex-1 text-center md:text-left">
                    <h2 className="text-2xl font-bold text-Charcoal-Gray dark:text-Pure-White">
                      {userProfile.full_name || userProfile.username || "Përdorues Pa Emër"}
                    </h2>
                    {userProfile.username && (
                      <p className="text-sm text-Medium-Gray dark:text-Light-Gray">@{userProfile.username}</p>
                    )}
                    <div className="mt-2 flex flex-col md:flex-row md:items-center gap-2 md:gap-4 text-sm text-Medium-Gray dark:text-Light-Gray">
                      <div className="flex items-center">
                        <Calendar size={16} className="mr-1" />
                        <span>Anëtar që nga {formatDate(userProfile.created_at)}</span>
                      </div>
                      {userProfile.location && (
                        <div className="flex items-center">
                          <MapPin size={16} className="mr-1" />
                          <span>{userProfile.location}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Buttons Section for Regular User */}
                  <div className="flex flex-col md:flex-row gap-3 mt-4 md:mt-0">
                    {user && userProfile.id !== user.id && (
                      <Button
                        onClick={handleStartChat}
                        className="bg-Primary-Purple hover:bg-Secondary-Purple text-Pure-White px-6 py-3 w-full md:w-auto"
                      >
                        <MessageCircle size={16} className="mr-2" />
                        Dërgo Mesazh
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              <Separator className="my-8" />
              <UserRatings userId={userProfile.id} />
              <Separator className="my-8" />
              <UserProductList 
                userId={userProfile.id} 
                isStore={userProfile.is_store}
                storeName={userProfile.store_name}
                username={userProfile.username}
                fullName={userProfile.full_name}
              />
            </Container>
          )
        ) : (
          !isLoading && userId && <Container className="py-10"><p>Profili i përdoruesit nuk mund të ngarkohej ose nuk u gjet.</p></Container>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default UserProfile;
